import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast, Toaster } from 'react-hot-toast';
import { apiClient } from '../services/api';
import { BASE_PATH } from '@/config/basePath';

const TURNSTILE_SITE_KEY = '0x4AAAAAAAfQpIzNWBpuiLSV';

const ResetPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState<'request' | 'confirm'>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileWidgetId = useRef<string | null>(null);
  const turnstileRendered = useRef(false);

  // Load Turnstile script and render single widget (only ever one)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const doRender = () => {
      if (turnstileRendered.current) return;
      const container = document.getElementById('turnstile-container');
      if (!container || !(window as any).turnstile) return;
      if (container.children.length > 0) return;

      turnstileRendered.current = true;
      const widgetId = (window as any).turnstile.render(container, {
        sitekey: TURNSTILE_SITE_KEY,
        size: 'normal',
        callback: (token: string) => setTurnstileToken(token),
        'expired-callback': () => setTurnstileToken(''),
        'error-callback': () => setTurnstileToken(''),
      });
      turnstileWidgetId.current = widgetId;
    };

    if ((window as any).turnstile) {
      doRender();
      return;
    }

    if (document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]')) {
      const check = setInterval(() => {
        if ((window as any).turnstile) {
          clearInterval(check);
          doRender();
        }
      }, 50);
      return () => clearInterval(check);
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    script.onload = doRender;
    document.head.appendChild(script);

    return () => {
      if (turnstileWidgetId.current && (window as any).turnstile) {
        (window as any).turnstile.remove(turnstileWidgetId.current);
        turnstileWidgetId.current = null;
      }
      turnstileRendered.current = false;
    };
  }, []);

  // Reset Turnstile when switching steps (single widget, fresh challenge each step)
  const prevStep = useRef(step);
  useEffect(() => {
    if (prevStep.current !== step && turnstileWidgetId.current != null && (window as any).turnstile) {
      prevStep.current = step;
      setTurnstileToken('');
      (window as any).turnstile.reset(turnstileWidgetId.current);
    } else {
      prevStep.current = step;
    }
  }, [step]);

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast.error(t('resetPassword.errorEnterEmail'));
      return;
    }
    setLoading(true);
    try {
      const result = await apiClient.requestResetCode(email, turnstileToken || '');
      if (result.ok) {
        setStep('confirm');
        setTurnstileToken('');
      } else {
        toast.error(result.error || t('resetPassword.errorSendCode'));
      }
    } catch {
      toast.error(t('resetPassword.errorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code || !newPassword) {
      toast.error(t('resetPassword.errorEnterCodeAndPassword'));
      return;
    }
    if (newPassword.length < 3) {
      toast.error(t('resetPassword.errorPasswordMin'));
      return;
    }
    setLoading(true);
    try {
      const result = await apiClient.setNewPassword(code, newPassword, turnstileToken || '');
      if (result.ok) {
        toast.success(result.success || t('resetPassword.successDefault'), { id: 'password-updated' });
        setTimeout(() => {
          window.location.href = `${BASE_PATH}/login`.replace(/\/+/g, '/') || '/';
        }, 1500);
      } else {
        toast.error(result.error || t('resetPassword.errorUpdatePassword'));
      }
    } catch {
      toast.error(t('resetPassword.errorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-sidebar p-4">
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
        }}
      />
      <div className="w-full max-w-md space-y-6">
        <div className="flex justify-center mb-2">
          <img
            src="https://folge.me/images/logo@2x.png"
            alt="Folge"
            className="h-20 w-auto"
          />
        </div>
        <Card>
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold">{t('resetPassword.title')}</CardTitle>
            <CardDescription>
              {step === 'request'
                ? t('resetPassword.descriptionRequest')
                : t('resetPassword.descriptionConfirm')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <form onSubmit={step === 'request' ? handleRequestCode : handleSetPassword} className="space-y-4">
              {step === 'request' ? (
                <div className="space-y-2">
                  <Label htmlFor="email">{t('resetPassword.email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder={t('resetPassword.emailPlaceholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                    autoComplete="email"
                  />
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="code">{t('resetPassword.codeLabel')}</Label>
                    <Input
                      id="code"
                      type="text"
                      placeholder={t('resetPassword.codePlaceholder')}
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      required
                      disabled={loading}
                      autoComplete="one-time-code"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="newPassword">{t('resetPassword.newPasswordLabel')}</Label>
                    <Input
                      id="newPassword"
                      type="password"
                      placeholder={t('resetPassword.passwordPlaceholder')}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={3}
                      disabled={loading}
                      autoComplete="new-password"
                    />
                  </div>
                </>
              )}
              <div id="turnstile-container" className="flex justify-center min-h-[65px]" />
              <Button
                type="submit"
                className="w-full"
                disabled={
                  loading ||
                  (step === 'request' ? !email : !code || !newPassword || newPassword.length < 3)
                }
              >
                {loading
                  ? step === 'request'
                    ? t('resetPassword.sending')
                    : t('resetPassword.updating')
                  : step === 'request'
                    ? t('resetPassword.requestCode')
                    : t('resetPassword.updatePassword')}
              </Button>
              {step === 'request' && (
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full text-muted-foreground"
                  disabled={loading}
                  onClick={() => setStep('confirm')}
                >
                  {t('resetPassword.haveCode')}
                </Button>
              )}
              {step === 'confirm' && (
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  disabled={loading}
                  onClick={() => {
                    setStep('request');
                    setCode('');
                    setNewPassword('');
                  }}
                >
                  {t('resetPassword.differentEmail')}
                </Button>
              )}
            </form>
          </CardContent>
        </Card>
        <p className="text-center text-sm text-muted-foreground">
          <Link to="/login" className="underline hover:text-foreground">
            {t('resetPassword.backToSignIn')}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
